'use client';

import { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/shared/components/Button';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from './Dialog';

interface ConfirmDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	title: string;
	description: ReactNode;
	cancelButtonText: string;
	confirmButtonText: string;
	isLoading?: boolean;
	loadingText?: string;
	variant?: 'default' | 'destructive';
}

export function ConfirmDialog({
	isOpen,
	onClose,
	onConfirm,
	title,
	description,
	cancelButtonText,
	confirmButtonText,
	isLoading = false,
	loadingText,
	variant = 'default',
}: ConfirmDialogProps) {
	const isDestructive = variant === 'destructive';

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent>
				<DialogHeader>
					<div className="flex items-center gap-3">
						{isDestructive && (
							<div className="w-12 h-12 shrink-0 rounded-full bg-red-500/20 backdrop-blur-sm flex items-center justify-center border border-red-400/30 shadow-lg shadow-red-500/20">
								<AlertTriangle className="w-6 h-6 text-red-400" />
							</div>
						)}
						<div>
							<DialogTitle>{title}</DialogTitle>
							<DialogDescription className="mt-1">
								{description}
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<DialogFooter className="gap-2">
					<Button
						variant="outline"
						onClick={onClose}
						disabled={isLoading}
						className="glass-light glass-hover border-white/20 text-white hover:bg-white/10"
					>
						{cancelButtonText}
					</Button>
					<Button
						variant={isDestructive ? 'destructive' : 'default'}
						onClick={onConfirm}
						disabled={isLoading}
						className={
							isDestructive
								? 'glass-frosted glass-hover bg-red-500/80 border border-red-400/50 hover:bg-red-600/90 text-white font-semibold shadow-lg shadow-red-500/20'
								: undefined
						}
					>
						{isLoading && loadingText
							? loadingText
							: confirmButtonText}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
